/**
 * Lazy AudioContext that survives iOS Safari's autoplay restrictions.
 * Must be unlocked from a user gesture (call `unlockAudio()` from a tap handler).
 */
let _ctx = null;

export function unlockAudio() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return false;
    if (!_ctx) _ctx = new AC();
    if (_ctx.state === 'suspended') _ctx.resume();
    return true;
  } catch {
    return false;
  }
}

/** Short beep — best-effort. Silent if AudioContext is not unlocked. */
export function beep({ frequency = 880, durationMs = 110, volume = 0.12 } = {}) {
  if (!_ctx) return false;
  try {
    const now = _ctx.currentTime;
    const osc = _ctx.createOscillator();
    const gain = _ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = frequency;
    // simple envelope to avoid clicks
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(volume, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + durationMs / 1000);
    osc.connect(gain).connect(_ctx.destination);
    osc.start(now);
    osc.stop(now + durationMs / 1000 + 0.02);
    return true;
  } catch {
    return false;
  }
}

/** Best-effort haptic — silent on iOS Safari. */
export function vibrate(pattern = [100, 50, 100]) {
  if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
    try { return navigator.vibrate(pattern); } catch { return false; }
  }
  return false;
}
